import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { PositionChangesReportData, Positions } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helper';

type PositionChangesReportEntry = {
  rowId: string;
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  positionName: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  status: string;
  hasAccount: boolean;
};

type PropsType = {
  data: PositionChangesReportData[];
  startDate: Date;
  endDate: Date;
};

export const usePositionChangesReport = (props: PropsType) => {
  const { data, startDate, endDate } = props;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();

  const formatPositionName = (name?: string): string => {
    if (!name) return '';
    return name
      .split('_')
      .map((part) => {
        const lower = part.toLowerCase();
        if (lower === 'hr') return 'HR';
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const rows = useMemo(() => {
    if (!data) return [];

    const rangeStart = startDate;
    const rangeEnd = endDate;

    const isWithinRangeInclusive = (date?: string | Date | null) => {
      if (!date) return false;
      return moment(date).isBetween(rangeStart, rangeEnd, 'date', '[]');
    };

    const sameDay = (a?: string | Date | null, b?: string | Date | null) => {
      if (!a || !b) return false;
      return moment(a).startOf('day').isSame(moment(b).startOf('day'));
    };

    return data.flatMap((entry): PositionChangesReportEntry[] => {
      const {
        fEmpId,
        fEmpFirstName,
        fEmpLastName,
        fEmpPositions,
        fEmpHasAccount,
      } = entry;

      if (!fEmpPositions || fEmpPositions.length === 0) return [];

      const positionsSorted = [...fEmpPositions].sort((a, b) => {
        const aStart = new Date(a.fEmpPrevPositionStartDate).getTime();
        const bStart = new Date(b.fEmpPrevPositionStartDate).getTime();
        return aStart - bStart;
      });

      const emp = getEmployeeById(fEmpId);
      const employeeNumber = emp?.fEmpPersonNumber || '';
      const firstStart = new Date(positionsSorted[0].fEmpPrevPositionStartDate);
      const employeeStartDateRaw = emp?.fEmplStartDate ?? undefined;

      const getStatusForPos = (pos: Positions): string => {
        if (pos?.fEmpPrevPositionEndDate) {
          return t('laEndedEmpl');
        }
        if (
          employeeStartDateRaw &&
          sameDay(pos.fEmpPrevPositionStartDate, employeeStartDateRaw)
        ) {
          return t('laStartedEmp');
        }
        return t('laPosition');
      };

      // Find all position rows that start within the selected period
      const positionsInRange = positionsSorted
        .map((pos, originalIdx) => ({ pos, originalIdx }))
        .filter(({ pos }) =>
          isWithinRangeInclusive(pos.fEmpPrevPositionStartDate),
        );

      // If we have changes in range, show them
      if (positionsInRange.length > 0) {
        return positionsInRange.map(({ pos, originalIdx }) => ({
          rowId: `${fEmpId}-${pos.fEmpPrevPositionStartDate}-${originalIdx}`,
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          employeeNumber,
          positionName: formatPositionName(pos.fEmpPrevPosition),
          startDate: convertToDateOrUndefined(pos.fEmpPrevPositionStartDate),
          endDate: convertToDateOrUndefined(pos.fEmpPrevPositionEndDate),
          status: getStatusForPos(pos),
          hasAccount: fEmpHasAccount,
        }));
      }

      // Otherwise, show employee only (empty row) if started before range
      if (firstStart < rangeStart) {
        return [
          {
            rowId: `${fEmpId}-empty-${moment(rangeStart).format('YYYYMMDD')}`,
            employeeId: fEmpId,
            firstName: fEmpFirstName,
            lastName: fEmpLastName,
            employeeNumber,
            positionName: '',
            startDate: undefined,
            endDate: undefined,
            status: '',
            hasAccount: fEmpHasAccount,
          },
        ];
      }

      return [];
    });
  }, [data, startDate, endDate, t, getEmployeeById]);

  const { columnConfigs } = useMemo(() => {
    const columnConfigs: GridColDef<PositionChangesReportEntry>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => (
          <EmployeeCellComponent
            {...params}
            pictureOnly={true}
            onClick={() => {
              goToEmployeeBasicDataView(params.row.employeeId);
            }}
          />
        ),
        minWidth: 60,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => {
              goToEmployeeBasicDataView(params.row.employeeId);
            }}
          />
        ),
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => {
              goToEmployeeBasicDataView(params.row.employeeId);
            }}
          />
        ),
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
      },
      {
        field: 'positionName',
        headerName: t('laPosition'),
        minWidth: 200,
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        type: 'date',
        minWidth: 120,
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'endDate',
        headerName: t('laEndDate'),
        type: 'date',
        minWidth: 120,
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        minWidth: 200,
        type: 'singleSelect',
        valueOptions: [t('laStartedEmp'), t('laEndedEmpl'), t('laPosition')],
      },
    ];

    return { columnConfigs };
  }, [t, goToEmployeeBasicDataView]);

  return {
    table: {
      columnConfigs,
      rows,
    },
  };
};
